// Rudras-AI Clover Terminal Payment Dialog — Milestone 4 (Feb 2026).
//
// Kiosk-side component that walks a customer through a Station Duo
// card payment. Reuses the existing shadcn/ui + toast conventions
// already used elsewhere in `components/retail/customer/`.
//
// Integration into CheckoutModal.jsx (documented in the CP4 arch doc):
//
//   1. Fetch tenant POS config (`GET /api/company/pos-config`) on
//      mount and expose "Clover Terminal" as a payment_method option
//      when `transaction_mode === "external_pos"` and
//      `provider === "clover"`.
//
//   2. When the customer submits with `paymentMethod === "clover_terminal"`,
//      call `onPlaced` for the order creation as usual, then mount
//      <CloverTerminalPaymentDialog orderId={order.id} onDone={...} />.
//
//   3. This dialog then owns the rest of the payment lifecycle: it
//      initiates the terminal session, polls status, and closes on
//      success / decline / cancel / reconciliation.

import React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Statuses coming from `payments/clover/terminal_repository.public_view`.
const IN_FLIGHT = new Set([
  "created",
  "awaiting_device",
  "claimed",
  "payment_in_progress",
]);
const TERMINAL_SUCCESS = "succeeded";
const TERMINAL_DECLINE = "declined";
const TERMINAL_CANCEL = "cancelled";
const TERMINAL_EXPIRED = "expired";
const TERMINAL_FAILED = "failed_terminal";
const TERMINAL_RECON = "reconciliation_required";

const POLL_MS = 2000;

/**
 * Props:
 *   orderId       — Rudras retail_orders.id (already-placed order).
 *   open          — controlled visibility (parent decides open/close).
 *   onDone(status, result) — called ONCE when the session reaches a
 *                             terminal state. `status` is one of:
 *                             "paid" | "declined" | "cancelled" |
 *                             "expired" | "failed" | "reconciliation".
 *   authToken     — Rudras JWT to use for the tenant admin endpoints.
 */
export default function CloverTerminalPaymentDialog({
  orderId,
  open,
  onDone,
  onSession,
  resumeSessionId,
  authToken,
}) {
  const [session, setSession] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [starting, setStarting] = React.useState(true);
  const timerRef = React.useRef(null);

  const client = React.useMemo(
    () =>
      axios.create({
        baseURL: API_BASE,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      }),
    [authToken]
  );

  // Kick off the session exactly once per open.
  React.useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;
    (async () => {
      try {
        setStarting(true);
        // Milestone 4C recovery — if the caller already knows a
        // session_id (from the durable localStorage recovery pointer), skip
        // initiation and resume polling against the existing row.
        // The backend GET is tenant-scoped and safe.
        if (resumeSessionId) {
          const { data } = await client.get(
            `/api/pos/clover/terminal/sessions/${resumeSessionId}`
          );
          if (cancelled) return;
          setSession(data.session);
          onSession?.(data.session);
          return;
        }
        const { data } = await client.post(
          `/api/pos/clover/terminal/sessions`,
          { order_id: orderId }
        );
        if (cancelled) return;
        setSession(data.session);
        onSession?.(data.session);
      } catch (e) {
        if (cancelled) return;
        const det = e?.response?.data?.detail;
        setError(
          (det && (det.message || det.code)) ||
            "Couldn't start Clover payment. Please retry."
        );
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, orderId, resumeSessionId, client, onSession]);

  // Poll for status while in flight.
  React.useEffect(() => {
    if (!session?.session_id) return;
    if (!IN_FLIGHT.has(session.status)) {
      // Reached terminal — notify parent exactly once.
      onDone?.(mapTerminal(session.status), session);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await client.get(
          `/api/pos/clover/terminal/sessions/${session.session_id}`
        );
        setSession(data.session);
      } catch (e) {
        // Transient error — keep polling; do NOT fail the payment.
      }
    }, POLL_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [session, client, onDone]);

  const canCancel =
    session &&
    (session.status === "created" || session.status === "awaiting_device");

  const onCancel = async () => {
    if (!session?.session_id) return;
    try {
      await client.post(
        `/api/pos/clover/terminal/sessions/${session.session_id}/cancel`
      );
      onDone?.("cancelled", session);
    } catch (e) {
      toast.error("Session could not be cancelled — it may have already started on the terminal.");
    }
  };

  return (
    <Dialog open={open} data-testid="clover-terminal-dialog">
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Clover Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2" data-testid="clover-terminal-body">
          <StateView session={session} error={error} starting={starting} />
        </div>
        <DialogFooter>
          {canCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid="clover-terminal-cancel-btn"
            >
              Cancel Payment
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StateView({ session, error, starting }) {
  if (error) {
    return (
      <div data-testid="clover-terminal-error" className="text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (starting || !session) {
    return (
      <Row icon={<Spinner />} label="Preparing payment…" testId="clover-terminal-preparing" />
    );
  }
  switch (session.status) {
    case "created":
    case "awaiting_device":
      return (
        <Row
          icon={<Spinner />}
          label="Please complete payment on the Clover terminal."
          testId="clover-terminal-awaiting"
        />
      );
    case "claimed":
    case "payment_in_progress":
      return (
        <Row icon={<Spinner />} label="Processing…" testId="clover-terminal-processing" />
      );
    case TERMINAL_SUCCESS:
      return (
        <Row
          icon={<Dot color="green" />}
          label="Paid."
          testId="clover-terminal-paid"
        />
      );
    case TERMINAL_DECLINE:
      return (
        <Row
          icon={<Dot color="red" />}
          label={"Card was declined." + (session.error_code ? ` (${session.error_code})` : "")}
          testId="clover-terminal-declined"
        />
      );
    case TERMINAL_CANCEL:
      return (
        <Row
          icon={<Dot color="gray" />}
          label="Payment cancelled."
          testId="clover-terminal-cancelled"
        />
      );
    case TERMINAL_EXPIRED:
      return (
        <Row
          icon={<Dot color="orange" />}
          label="Payment timed out. Please start a new payment."
          testId="clover-terminal-expired"
        />
      );
    case TERMINAL_FAILED:
      return (
        <Row
          icon={<Dot color="red" />}
          label={"Payment could not complete." + (session.error_code ? ` (${session.error_code})` : "")}
          testId="clover-terminal-failed"
        />
      );
    case TERMINAL_RECON:
      return (
        <Row
          icon={<Dot color="orange" />}
          label="This payment needs staff assistance to reconcile. Please ask an employee."
          testId="clover-terminal-reconciliation"
        />
      );
    default:
      return (
        <Row
          icon={<Spinner />}
          label={`Status: ${session.status}`}
          testId="clover-terminal-unknown"
        />
      );
  }
}

function Row({ icon, label, testId }) {
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <span>{icon}</span>
      <span className="text-sm">{label}</span>
    </div>
  );
}

function Spinner() {
  return <Loader2 className="h-5 w-5 animate-spin text-orange-500" />;
}

function Dot({ color }) {
  const cls = {
    green: "bg-emerald-500",
    red: "bg-red-500",
    orange: "bg-orange-500",
    gray: "bg-slate-400",
  }[color];
  return <span className={"inline-block h-3 w-3 rounded-full " + cls} />;
}

function mapTerminal(status) {
  switch (status) {
    case TERMINAL_SUCCESS:
      return "paid";
    case TERMINAL_DECLINE:
      return "declined";
    case TERMINAL_CANCEL:
      return "cancelled";
    case TERMINAL_EXPIRED:
      return "expired";
    case TERMINAL_RECON:
      return "reconciliation";
    default:
      return "failed";
  }
}
